import React, { useState, useEffect, useRef } from 'react';
import { OperationBC, OperationFormulaire } from '../types';
import { libelleTypeCommande } from '../domain/complements';
import { adresseUneLigne } from '../../../utils/adresse';

// Sous-bloc "Operation" du Descriptif administratif — COPIE FIDELE de la refonte MS
// (SuiviOperationMSV2/components/OperationFormulaireAdmin), grille 12 colonnes (span-3 = 4 champs
// par ligne). ISOLE : aucun import MS ; les helpers (dates, validation, constante) sont inlines.
// Lit/ecrit le sac formulaire (OperationFormulaire) ; viewMode decide par le parent. Conserve
// l'autocomplete adresse IGN et le recalcul des dates.

const CHAMP_PHRASE_MAX = 255;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (value: string): boolean => EMAIL_RE.test(value.trim());
const isValidPhoneFr = (value: string): boolean => /^0[1-9]\d{8}$/.test(value.replace(/\D/g, ''));
const FORMAT_ERROR = {
  moeMail: 'Adresse mail invalide (ex : john.smith@gmail.com).',
  moeTelephone: 'Téléphone invalide (10 chiffres, ex : 0678901234).',
};

const isoDate = (d: Date): string => d.toISOString().split('T')[0];
const addDays = (base: Date, days: number): Date => {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
};
const computeFinConsultation = (dateDebutConsultation: string | undefined, delaisConsultation: number): string =>
  isoDate(addDays(dateDebutConsultation ? new Date(dateDebutConsultation) : new Date(), delaisConsultation));
const computeDateDebutOperation = (delaisConsultation: number): string => isoDate(addDays(new Date(), delaisConsultation));
const computeDateFinOperation = (dateDebutOperation: string, delaisFin: number): string =>
  isoDate(addDays(new Date(dateDebutOperation), delaisFin));

interface IgnSuggestion {
  label: string;
  postcode?: string;
  city?: string;
}
interface IgnApiResult {
  fulltext?: string;
  zipcode?: string;
  city?: string;
}

interface OperationFormulaireAdminProps {
  operation: OperationBC;
  formData: OperationFormulaire;
  viewMode: boolean;
  errors?: Record<string, string>;
  setFormData?: (data: OperationFormulaire) => void;
}

const OperationFormulaireAdmin: React.FC<OperationFormulaireAdminProps> = ({
  operation,
  formData,
  viewMode,
  errors = {},
  setFormData,
}) => {
  // Ce qui est AFFICHE dans le champ adresse : la ligne complete (voie, code postal ville, pays).
  // Ce qui est PERSISTE reste `formData.adresse` = la voie seule, les trois autres gardant leur
  // colonne. Sans cette dissociation, choisir une suggestion ecrirait la ville et le pays dans la
  // colonne adresse, et on les retrouverait en double a la lecture.
  const adresseComplete = adresseUneLigne(formData.adresse, formData.codePostal, formData.ville, formData.pays);
  const [adresseInput, setAdresseInput] = useState(adresseComplete);
  const [adresseSuggestions, setAdresseSuggestions] = useState<IgnSuggestion[]>([]);
  const [touched, setTouched] = useState<{ moeMail?: boolean; moeTelephone?: boolean }>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSelectingRef = useRef(false);
  const hasUserTypedRef = useRef(false);
  const hasManuallyEditedDateRef = useRef(false);

  const update = (patch: Partial<OperationFormulaire>) => {
    if (setFormData) setFormData({ ...formData, ...patch });
  };

  useEffect(() => {
    if (!hasUserTypedRef.current) {
      setAdresseSuggestions([]);
      return;
    }
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }
    if (adresseInput.trim().length < 3) {
      setAdresseSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://data.geopf.fr/geocodage/completion?text=${encodeURIComponent(adresseInput)}&index=StreetAddress&limit=5`,
        );
        if (response.ok) {
          const json = (await response.json()) as { results?: IgnApiResult[] };
          const suggestions: IgnSuggestion[] = (json.results || []).map((item) => ({
            label: item.fulltext || '',
            postcode: item.zipcode || '',
            city: item.city || '',
          }));
          setAdresseSuggestions(suggestions);
        }
      } catch {
        setAdresseSuggestions([]);
      }
    }, 300);
  }, [adresseInput]);

  // Resynchronise le champ quand l'operation est (re)hydratee depuis l'API. On compare la ligne
  // COMPLETE : comparer la seule voie remettrait le champ a la voie juste apres le choix d'une
  // suggestion, effacant a l'ecran le code postal et la ville qu'on vient d'y mettre.
  useEffect(() => {
    if (adresseComplete !== adresseInput) setAdresseInput(adresseComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adresseComplete]);

  const handleAdresseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasUserTypedRef.current = true;
    setAdresseInput(e.target.value);
    // Taper librement INVALIDE la derivation : code postal, ville et pays ne sont plus affiches,
    // ils ne peuvent donc etre renseignes que par une suggestion IGN. Les vider ici est ce qui rend
    // la regle verifiable — sinon une adresse tapee a la main conserverait le code postal d'une
    // adresse precedente, et partirait en base avec une localisation fausse.
    update({ adresse: e.target.value, codePostal: '', ville: '', pays: '' });
  };

  const handleSelectAdresse = (suggestion: IgnSuggestion) => {
    isSelectingRef.current = true;
    hasUserTypedRef.current = false;
    const adresse = suggestion.label.includes(',') ? suggestion.label.split(',')[0].trim() : suggestion.label;
    const codePostal = suggestion.postcode || '';
    const ville = suggestion.city || '';
    update({ adresse, codePostal, ville, pays: 'France' });
    // Le champ affiche l'adresse ENTIERE une fois la suggestion retenue : c'est le seul retour visuel
    // qui confirme quelle commune a ete geocodee. La voie seule laisserait croire que le choix n'a
    // pas ete pris en compte, deux rues homonymes etant alors indistinguables a l'ecran.
    setAdresseInput(adresseUneLigne(adresse, codePostal, ville, 'France'));
    setAdresseSuggestions([]);
  };

  const finConsultationStr = computeFinConsultation(
    formData.dateDebutConsultation,
    Number(formData.delaisConsultation || 10),
  );

  // Ces deux effets DERIVENT les dates d'operation des delais saisis. Ils ne doivent le faire que
  // pour une date encore vierge : depuis que le socle pre-rempli expose les dates PERSISTEES
  // (refonteFromOperation), un recalcul au montage ecraserait la date enregistree par une date
  // relative a aujourd'hui — silencieusement, a la simple ouverture du brouillon.
  // La garde ne vaut qu'au PREMIER passage : un changement ulterieur de delai doit bien recalculer.
  const datesInitialiseesRef = useRef(false);

  useEffect(() => {
    if (!setFormData || viewMode || hasManuallyEditedDateRef.current) return;
    if (!datesInitialiseesRef.current) {
      datesInitialiseesRef.current = true;
      if (formData.dateDebutOperation) return; // valeur persistee : on la respecte
    }
    const debutStr = computeDateDebutOperation(Number(formData.delaisConsultation || 10));
    if (formData.dateDebutOperation !== debutStr) {
      setFormData({ ...formData, dateDebutOperation: debutStr });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.delaisConsultation, viewMode, setFormData]);

  const dateFinInitialiseeRef = useRef(false);

  useEffect(() => {
    if (!setFormData || viewMode || !formData.dateDebutOperation) return;
    if (!dateFinInitialiseeRef.current) {
      dateFinInitialiseeRef.current = true;
      if (formData.dateFinOperation) return; // valeur persistee : on la respecte
    }
    const finStr = computeDateFinOperation(formData.dateDebutOperation, Number(formData.delaisFinOperation || 10));
    if (formData.dateFinOperation !== finStr) {
      setFormData({ ...formData, dateFinOperation: finStr });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.dateDebutOperation, formData.delaisFinOperation, viewMode, setFormData]);

  const dateFr = (value?: string): string => (value ? new Date(value).toLocaleDateString('fr-FR') : '');

  const mailError = touched.moeMail
    ? (formData.moeMail?.trim() && !isValidEmail(formData.moeMail) ? FORMAT_ERROR.moeMail : '')
    : (errors.moeMail || '');
  const telError = touched.moeTelephone
    ? (formData.moeTelephone?.trim() && !isValidPhoneFr(formData.moeTelephone) ? FORMAT_ERROR.moeTelephone : '')
    : (errors.moeTelephone || '');

  return (
    <div className={`bcadm-desc__grid${viewMode ? ' view-mode' : ''}`}>
      {/* Ligne 1 : Nom de l'ouvrage | Nom de l'operation | Reference interne | Type de commande */}
      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Nom de l'ouvrage {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
        {viewMode ? (
          <div className="input-inactif">{formData.nomOuvrage || '-'}</div>
        ) : (
          <>
            <input
              type="text"
              maxLength={CHAMP_PHRASE_MAX}
              value={formData.nomOuvrage || ''}
              onChange={(e) => update({ nomOuvrage: e.target.value })}
              className={errors.nomOuvrage ? 'error' : ''}
              placeholder="ex : Centre Hospitalier Sainte Justine"
            />
            <span className="bcadm-char-count">{(formData.nomOuvrage || '').length}/{CHAMP_PHRASE_MAX}</span>
            {errors.nomOuvrage && <span className="error-message">{errors.nomOuvrage}</span>}
          </>
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Nom de l'opération {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
        {viewMode ? (
          <div className="input-inactif">{formData.nomOperation || '-'}</div>
        ) : (
          <>
            <input
              type="text"
              maxLength={CHAMP_PHRASE_MAX}
              value={formData.nomOperation || ''}
              onChange={(e) => update({ nomOperation: e.target.value })}
              className={errors.nomOperation ? 'error' : ''}
              placeholder="ex : Réhabilitation de la partie sud est"
            />
            <span className="bcadm-char-count">{(formData.nomOperation || '').length}/{CHAMP_PHRASE_MAX}</span>
            {errors.nomOperation && <span className="error-message">{errors.nomOperation}</span>}
          </>
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>N° Référence interne</label>
        {viewMode ? (
          <div className="input-inactif">{formData.referenceInterne || '-'}</div>
        ) : (
          <input
            type="text"
            maxLength={100}
            value={formData.referenceInterne || ''}
            onChange={(e) => update({ referenceInterne: e.target.value })}
            placeholder="ex : 23456789009876"
          />
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        {/* Type de commande : derive de operation.type_commande (Initiale, ou « Complémentaire de
            <numero de la principale> »), lecture seule : seule la creation d'un complement le pose. */}
        <label>Type de commande</label>
        <div className="input-inactif">{libelleTypeCommande(operation.type_commande)}</div>
      </div>

      {/* Ligne 2 : Adresse (une seule ligne) */}
      {/* Adresse sur UNE ligne, pleine largeur : code postal, ville et pays ne sont plus des champs
          a part, ils sont derives de la suggestion IGN choisie (cf. handleSelectAdresse). Le back
          continue de recevoir les trois separement — seul l'affichage change.
          En LECTURE SEULE ils reapparaissent, composes dans la meme ligne : sans eux on ne lirait que
          la voie, et l'operation perdrait sa localisation a l'ecran comme a l'impression. */}
      <div className="form-group" style={{ gridColumn: 'span 12' }}>
        <label>Adresse de l'opération {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
        {viewMode ? (
          <div className="input-inactif">{adresseComplete || '-'}</div>
        ) : (
          <div className="autocomplete-container">
            <input
              type="text"
              id="adresse-autocomplete"
              value={adresseInput}
              onChange={handleAdresseInputChange}
              className={errors.adresse ? 'error' : ''}
              placeholder="ex : 43 Chemin des étoiles"
              autoComplete="off"
            />
            {adresseSuggestions.length > 0 && (
              <ul className="autocomplete-list" role="listbox">
                {adresseSuggestions.map((suggestion, index) => (
                  <li key={index} role="option" aria-selected={false} onClick={() => handleSelectAdresse(suggestion)}>
                    {suggestion.label}
                  </li>
                ))}
              </ul>
            )}
            {errors.adresse && <span className="error-message">{errors.adresse}</span>}
          </div>
        )}
      </div>

      {/* Code postal, ville et pays : DERIVES de la suggestion IGN, plus affiches. Ils restent
          dans le formulaire et partent en base comme avant — seul le rendu change, l'adresse
          tenant desormais sur une seule ligne. Les masquer impose de valider l'adresse par une
          suggestion : une saisie libre les vide (cf. handleAdresseInputChange), et l'erreur
          remonte sur le champ Adresse, le seul visible. */}

      {/* Ligne 3 : Cabinet de maitrise d'oeuvre | Interlocuteur | Mail | Telephone */}
      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Cabinet de maitrise d'œuvre éventuel ?</label>
        {viewMode ? (
          <div className="input-inactif">{formData.coordonneesMaitrise || '-'}</div>
        ) : (
          <>
            <input
              type="text"
              maxLength={CHAMP_PHRASE_MAX}
              value={formData.coordonneesMaitrise || ''}
              onChange={(e) => update({ coordonneesMaitrise: e.target.value })}
              placeholder="ex : Cabinet OREO"
            />
            <span className="bcadm-char-count">{(formData.coordonneesMaitrise || '').length}/{CHAMP_PHRASE_MAX}</span>
          </>
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Interlocuteur</label>
        {viewMode ? (
          <div className="input-inactif">{formData.moeInterlocuteur || '-'}</div>
        ) : (
          <>
            <input
              type="text"
              maxLength={CHAMP_PHRASE_MAX}
              value={formData.moeInterlocuteur || ''}
              onChange={(e) => update({ moeInterlocuteur: e.target.value })}
              placeholder="ex : John SMITH"
            />
            <span className="bcadm-char-count">{(formData.moeInterlocuteur || '').length}/{CHAMP_PHRASE_MAX}</span>
          </>
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Mail</label>
        {viewMode ? (
          <div className="input-inactif">{formData.moeMail || '-'}</div>
        ) : (
          <>
            <input
              type="email"
              maxLength={255}
              value={formData.moeMail || ''}
              onChange={(e) => update({ moeMail: e.target.value })}
              onBlur={() => setTouched((t) => ({ ...t, moeMail: true }))}
              className={mailError ? 'error' : ''}
              placeholder="ex : john.smith@gmail.com"
            />
            {mailError && <span className="error-message">{mailError}</span>}
          </>
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Téléphone</label>
        {viewMode ? (
          <div className="input-inactif">{formData.moeTelephone || '-'}</div>
        ) : (
          <>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={formData.moeTelephone || ''}
              onChange={(e) => update({ moeTelephone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              onBlur={() => setTouched((t) => ({ ...t, moeTelephone: true }))}
              className={telError ? 'error' : ''}
              placeholder="ex : 0678901234"
            />
            {telError && <span className="error-message">{telError}</span>}
          </>
        )}
      </div>

      {/* Ligne 4 : Date demarrage | Duree previsionnelle | Date fin (calculee) | Phases */}
      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Date prévisionnelle de démarrage {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
        {viewMode ? (
          <div className="input-inactif">{dateFr(formData.dateDebutOperation)}</div>
        ) : (
          <>
            <input
              type="date"
              min={finConsultationStr}
              value={formData.dateDebutOperation || ''}
              onChange={(e) => {
                hasManuallyEditedDateRef.current = true;
                update({ dateDebutOperation: e.target.value });
              }}
              className={`date-input${errors.dateDebutOperation ? ' error' : ''}`}
            />
            {/* Ce champ est OBLIGATOIRE (validateConsultationForm) mais ne lisait pas `errors` :
                laisse vide, le lancement s'arretait sans rien dire. Pire, `scrollToFirstError` cherche
                `.error`/`.error-message` — n'en trouvant aucun, il ne scrollait nulle part : le clic
                paraissait sans effet. */}
            {errors.dateDebutOperation && (
              <span className="error-message">{errors.dateDebutOperation}</span>
            )}
          </>
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Durée prévisionnelle (jours calendaires) {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
        {viewMode ? (
          <div className="input-inactif">{`${formData.delaisFinOperation || 10} jours`}</div>
        ) : (
          <>
            <input
              type="number"
              min={10}
              value={formData.delaisFinOperation || 10}
              onChange={(e) => update({ delaisFinOperation: Math.max(10, Number(e.target.value)) })}
              className={errors.delaisFinOperation ? 'error' : ''}
              placeholder="ex : 456"
            />
            {/* Inatteignable par la saisie (l'onChange force >= 10, le defaut vaut 10), mais un
                brouillon enregistre avant cette regle peut porter une valeur plus basse : la
                validation le refuserait alors sans que le champ ne dise rien. */}
            {errors.delaisFinOperation && (
              <span className="error-message">{errors.delaisFinOperation}</span>
            )}
          </>
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Date prévisionnelle de fin d'opération</label>
        <div className="input-inactif">{dateFr(formData.dateFinOperation)}</div>
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Phases ?</label>
        {viewMode ? (
          <div className="input-inactif">{formData.phases || '-'}</div>
        ) : (
          <>
            <input
              type="text"
              maxLength={CHAMP_PHRASE_MAX}
              value={formData.phases || ''}
              onChange={(e) => update({ phases: e.target.value })}
              placeholder="ex : 4 phases opérationnelles"
            />
            <span className="bcadm-char-count">{(formData.phases || '').length}/{CHAMP_PHRASE_MAX}</span>
          </>
        )}
      </div>

    </div>
  );
};

export default OperationFormulaireAdmin;
